import { supabase } from '../../../services/supabaseClient';

export const INTEGRATION_PROVIDERS = {
    GOOGLE: 'google',
    ZOOM: 'zoom'
};

const getZoomClientId = () => process.env.REACT_APP_ZOOM_CLIENT_ID || '';

const getZoomRedirectUri = () =>
    process.env.REACT_APP_ZOOM_REDIRECT_URI ||
    (typeof window !== 'undefined' ? `${window.location.origin}/integrations/callback` : '/integrations/callback');

/**
 * Builds the Zoom OAuth authorization URL.
 * Must match exactly: https://zoom.us/oauth/authorize?response_type=code&client_id=...&redirect_uri=http://localhost:3000/integrations/callback
 * (Zoom General App accepts unencoded redirect_uri)
 */
const buildZoomAuthUrl = () => {
    const clientId = getZoomClientId();
    const redirectUri = getZoomRedirectUri();
    return `https://zoom.us/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
};

export const integrationService = {
    /**
     * Provider configurations for UI and Logic
     */
    getProviderConfig: (provider) => {
        const configs = {
            [INTEGRATION_PROVIDERS.GOOGLE]: {
                name: 'Google Calendar & Meet',
                icon: 'google',
                description: 'Connect your Google account to automatically detect and join Google Meet calls',
                permissions: [
                    'Read your calendar events',
                    'Join Google Meet meetings on your behalf',
                    'Record meeting audio and video',
                    'Generate AI transcripts and summaries'
                ]
                // authUrl removed, handled via Supabase OAuth
            },
            [INTEGRATION_PROVIDERS.ZOOM]: {
                name: 'Zoom',
                icon: 'zoom',
                description: 'Connect your Zoom account to automatically detect and join Zoom meetings',
                permissions: [
                    'Read your upcoming meetings',
                    'Join Zoom meetings on your behalf',
                    'Record meeting audio and video',
                    'Generate AI transcripts and summaries'
                ],
                authUrl: buildZoomAuthUrl()
            }
        };
        return configs[provider];
    },

    /**
     * Initiates OAuth flow for a provider
     */
    connect: async (provider) => {
        if (provider === INTEGRATION_PROVIDERS.GOOGLE) {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    scopes: 'openid email https://www.googleapis.com/auth/calendar.readonly',
                    queryParams: {
                        access_type: 'offline', // Required for refresh_token
                        prompt: 'consent', // Force consent screen every time to ensure refresh_token is always returned
                    },
                    redirectTo: `${window.location.origin}/integrations/callback`
                }
            });
            if (error) console.error('Supabase Google OAuth error:', error);
        }

        if (provider === INTEGRATION_PROVIDERS.ZOOM) {
            const clientId = getZoomClientId();
            if (!clientId || clientId === 'PENDING_CLIENT_ID') {
                const msg = 'Zoom OAuth: REACT_APP_ZOOM_CLIENT_ID is missing. Add it to .env and restart the dev server.';
                console.error(msg);
                if (typeof window !== 'undefined') window.alert(msg);
                return;
            }
            const config = integrationService.getProviderConfig(provider);
            if (config?.authUrl) {
                window.location.href = config.authUrl;
            }
        }
    },

    /**
     * Saves tokens and session data to Supabase users table
     */
    saveTokens: async (userId, provider, tokenData) => {
        console.log('💾 [Save Tokens] Starting save for:', { userId, provider, hasAccessToken: !!tokenData.access_token, hasRefreshToken: !!tokenData.refresh_token });

        const data = {};

        if (provider === INTEGRATION_PROVIDERS.GOOGLE) {
            data.google_connected = true;
            // Store tokens if provided (from Supabase session)
            if (tokenData.access_token) {
                data.google_access_token = tokenData.access_token;
            }
            if (tokenData.refresh_token) {
                data.google_refresh_token = tokenData.refresh_token;
            }
            if (tokenData.expires_in) {
                data.google_token_expires_at = Date.now() + (tokenData.expires_in * 1000);
            }

            console.log('📝 [Save Tokens] Google data to save:', {
                google_connected: data.google_connected,
                hasAccessToken: !!data.google_access_token,
                hasRefreshToken: !!data.google_refresh_token,
                expiresAt: data.google_token_expires_at
            });
        }

        if (provider === INTEGRATION_PROVIDERS.ZOOM) {
            data.zoom_access_token = tokenData.access_token;
            data.zoom_refresh_token = tokenData.refresh_token;
            data.zoom_token_expires_at = Date.now() + (tokenData.expires_in * 1000);
            data.zoom_connected = true;

            console.log('📝 [Save Tokens] Zoom data to save:', {
                zoom_connected: data.zoom_connected,
                hasAccessToken: !!data.zoom_access_token,
                hasRefreshToken: !!data.zoom_refresh_token
            });
        }

        const { error, data: updateResult } = await supabase
            .from('users')
            .update(data)
            .eq('user_id', userId)
            .select();

        if (error) {
            console.error(`❌ [Save Tokens] Error saving ${provider} connection status:`, error);
            throw error;
        }

        console.log('✅ [Save Tokens] Successfully saved tokens. Update result:', updateResult);
        return true;
    },

    /**
     * Disconnects an integration by removing tokens
     */
    disconnect: async (userId, provider) => {
        const data = {};

        if (provider === INTEGRATION_PROVIDERS.GOOGLE) {
            data.google_access_token = null;
            data.google_refresh_token = null;
            data.google_token_expires_at = null;
            data.google_connected = false;
        }

        if (provider === INTEGRATION_PROVIDERS.ZOOM) {
            data.zoom_access_token = null;
            data.zoom_refresh_token = null;
            data.zoom_token_expires_at = null;
            data.zoom_connected = false;
        }

        const { error } = await supabase
            .from('users')
            .update(data)
            .eq('user_id', userId);

        if (error) {
            console.error(`Error disconnecting ${provider}:`, error);
            throw error;
        }

        return true;
    },

    /**
     * Fetches dashboard stats based on connection status
     */
    getDashboardStats: async (userId) => {
        try {
            // Add timeout to prevent hanging
            const queryPromise = supabase
                .from('users')
                .select('google_connected, zoom_connected')
                .eq('user_id', userId)
                .maybeSingle();

            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Dashboard stats query timed out')), 5000)
            );

            const { data: user, error } = await Promise.race([
                queryPromise,
                timeoutPromise
            ]);

            if (error || !user) return null;

            return {
                meetingsScheduledToday: 0,
                stats: {
                    completed: 0,
                    recordingNow: 0,
                    upcoming: 0,
                    thisWeek: 0,
                    attendanceRate: "0%"
                }
            };
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            // Return default stats instead of throwing to prevent UI blocking
            return {
                meetingsScheduledToday: 0,
                stats: {
                    completed: 0,
                    recordingNow: 0,
                    upcoming: 0,
                    thisWeek: 0,
                    attendanceRate: "0%"
                }
            };
        }
    },

    /**
     * Exchange OAuth authorization code for tokens using Edge Function
     */
    exchangeCode: async (userId, provider, code, redirectUri) => {
        const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
        
        if (!SUPABASE_URL) {
            console.error('❌ [ExchangeCode] REACT_APP_SUPABASE_URL is not defined');
            throw new Error('Supabase URL not configured. Please check your .env file.');
        }
        
        const SUPABASE_FUNCTION_URL = `${SUPABASE_URL}/functions/v1/refresh-oauth-token`;
        console.log('🔄 [ExchangeCode] Calling edge function:', SUPABASE_FUNCTION_URL);
        
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;

        if (!accessToken) {
            console.error('❌ [ExchangeCode] No session access token found');
            throw new Error('No session found. Please log in again.');
        }

        console.log('📤 [ExchangeCode] Sending request:', {
            url: SUPABASE_FUNCTION_URL,
            provider,
            hasCode: !!code,
            redirectUri
        });

        try {
            // Add timeout to prevent hanging
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

            const response = await fetch(SUPABASE_FUNCTION_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    provider: provider,
                    code: code,
                    redirect_uri: redirectUri
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);
            console.log('📥 [ExchangeCode] Response status:', response.status, response.statusText);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ [ExchangeCode] Error response:', errorText);
                throw new Error(`Code exchange failed (${response.status}): ${errorText}`);
            }

            const data = await response.json();
            console.log('✅ [ExchangeCode] Success:', { hasAccessToken: !!data.access_token, hasRefreshToken: !!data.refresh_token });
            return data;
        } catch (fetchError) {
            console.error('❌ [ExchangeCode] Fetch error:', fetchError);
            if (fetchError.name === 'AbortError') {
                throw new Error('Request timed out. Please check if the edge function is deployed and accessible.');
            }
            if (fetchError.message) {
                throw fetchError;
            }
            throw new Error(`Network error: ${fetchError.toString()}`);
        }
    },

    /**
     * Refresh OAuth token for a provider using Edge Function
     */
    refreshToken: async (userId, provider = 'google') => {
        const SUPABASE_FUNCTION_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/refresh-oauth-token`;
        
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;

        if (!accessToken) {
            throw new Error('No session found');
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        try {
            const response = await fetch(SUPABASE_FUNCTION_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify({
                    provider: provider
                }),
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorText = await response.text();
                const errorMessage = errorText.toLowerCase();
                
                if (response.status === 400 && (errorMessage.includes('invalid_refresh_token') || errorMessage.includes('invalid') || errorMessage.includes('expired'))) {
                    const updateData = {};
                    if (provider === 'google') {
                        updateData.google_connected = false;
                        updateData.google_is_active = false;
                        updateData.google_access_token = null;
                        updateData.google_refresh_token = null;
                        updateData.google_token_expires_at = null;
                    } else if (provider === 'zoom') {
                        updateData.zoom_connected = false;
                        updateData.zoom_is_active = false;
                        updateData.zoom_access_token = null;
                        updateData.zoom_refresh_token = null;
                        updateData.zoom_token_expires_at = null;
                    }
                    
                    await supabase
                        .from('users')
                        .update(updateData)
                        .eq('user_id', userId);
                    
                    console.warn(`Invalid refresh token for ${provider}, marking as disconnected`);
                }
                
                throw new Error(`Token refresh failed: ${errorText}`);
            }

            const data = await response.json();
            return data.access_token;
        } catch (fetchError) {
            clearTimeout(timeoutId);
            if (fetchError.name === 'AbortError') {
                throw new Error('Token refresh timed out');
            }
            throw fetchError;
        }
    },

    /**
     * Refresh all tokens on login (Google and Zoom if connected)
     */
    refreshTokensOnLogin: async (userId) => {
        try {
            const { data: user, error: rowError } = await supabase
                .from('users')
                .select('*')
                .eq('user_id', userId)
                .maybeSingle();

            if (rowError) {
                console.warn('refreshTokensOnLogin: users row not readable, skipping OAuth refresh:', rowError.message);
                return;
            }

            if (!user) return;

            const refreshPromises = [];

            if (user.google_refresh_token) {
                refreshPromises.push(
                    integrationService.refreshToken(userId, 'google')
                        .catch(err => {
                            console.warn('Failed to refresh Google token:', err);
                            return null;
                        })
                );
            }

            if (user.zoom_refresh_token) {
                refreshPromises.push(
                    integrationService.refreshToken(userId, 'zoom')
                        .catch(err => {
                            console.warn('Failed to refresh Zoom token:', err);
                            return null;
                        })
                );
            }

            await Promise.allSettled(refreshPromises);
            console.log('✅ Tokens refreshed on login');
        } catch (error) {
            console.error('Error refreshing tokens on login:', error);
        }
    }
};
